const text = value => String(value ?? "")
const filled = value => !!text(value).trim()

export function normalizeHandoutConfig(config = {}) {
	if (!config.handoutConfig) return config
	const handout = config.handoutConfig
	return {
		...config,
		fields: Array.isArray(config.fields) ? config.fields : [],
		handoutConfig: {
			...handout,
			sections: (Array.isArray(handout.sections) ? handout.sections : []).map(section => ({...section, id: text(section.id), title: text(section.title), rules: (Array.isArray(section.rules) ? section.rules : []).map(rule => ({id: text(rule.id), text: text(rule.text)}))})),
			focusOptions: (Array.isArray(handout.focusOptions) ? handout.focusOptions : []).map(option => ({...option, id: text(option.id), title: text(option.title), sectionId: text(option.sectionId)})),
			context: text(handout.context),
			permittedBehavior: text(handout.permittedBehavior),
			specialInstructions: text(handout.specialInstructions),
			acknowledgementEnabled: handout.acknowledgementEnabled === true,
			labels: {...handout.labels},
		},
	}
}

export function getHandoutConfigErrors(config = {}) {
	if (!config.handoutConfig) return []
	const handout = normalizeHandoutConfig(config).handoutConfig
	const sections = handout.sections
	const options = handout.focusOptions
	const ids = [...sections, ...sections.flatMap(section => section.rules), ...options].map(item => item.id)
	if (ids.some(id => !id) || new Set(ids).size !== ids.length) return ["Handout sections, rules, and focuses need unique IDs"]
	if (sections.some(section => !filled(section.title)) || options.some(option => !filled(option.title))) return ["Handout sections and focuses need titles"]
	if (options.some(option => option.sectionId && !sections.some(section => section.id === option.sectionId))) return ["A ritual focus references a removed section"]
	if (options.length && !options.some(option => option.id === handout.defaultFocusId)) return ["Select an available default ritual focus"]
	if (!filled(handout.questionId) || !filled(handout.questionText)) return ["Patient question identity and wording are required"]
	return []
}

export function getHandoutView(config = {}, payload = {}) {
	const handout = normalizeHandoutConfig(config).handoutConfig || {}
	const selectedFocusId = payload.selectedFocusId || handout.defaultFocusId || ""
	const focus = (handout.focusOptions || []).find(option => option.id === selectedFocusId)
	const context = text(payload.context ?? handout.context)
	const sections = (handout.sections || []).filter(section => !focus?.sectionId || section.id === focus.sectionId)
	const rules = sections.flatMap(section => section.rules.filter(rule => filled(rule.text)))
	const missing = []
	if (!focus) missing.push(handout.labels?.focus || "Ritual focus")
	if (!filled(context)) missing.push(handout.labels?.context || "Practice context")
	if (!rules.length) missing.push(handout.labels?.rules || "Ritual-prevention rules")
	const signature = JSON.stringify({focus: [focus?.id, focus?.title], context, sections: sections.map(section => [section.id, section.title, section.rules]), permittedBehavior: handout.permittedBehavior, specialInstructions: handout.specialInstructions, acknowledgementEnabled: handout.acknowledgementEnabled})
	const confirmationSignature = JSON.stringify({signature, title: config.title || "", instructions: config.instructions || "", questionText: handout.questionText, acknowledgement: handout.labels?.acknowledgement || ""})
	const previousSignature = payload.acknowledgedSignature || (payload.acknowledged && payload.form?.handoutConfig ? getHandoutView(payload.form).confirmationSignature : "")
	const acknowledged = payload.acknowledged === true && handout.acknowledgementEnabled && previousSignature === confirmationSignature
	return {handout, focus, context, sections, rules, missing, signature, confirmationSignature, isReady: !missing.length && handout.readySignature === signature, acknowledged, acknowledgedAt: acknowledged ? text(payload.acknowledgedAt) : "", questions: text(payload.answers?.[handout.questionId])}
}

export function updateHandoutReady(config = {}, ready = false) {
	const normalized = normalizeHandoutConfig(config)
	const view = getHandoutView(normalized)
	if (ready && view.missing.length) throw new Error(`Complete ${view.missing.join(", ")} before marking ready`)
	return {...normalized, handoutConfig: {...normalized.handoutConfig, readySignature: ready ? view.signature : ""}}
}

export function editHandoutCollection(config = {}, kind = "sections", operation = "add", index = 0, sectionId = "") {
	const normalized = normalizeHandoutConfig(config)
	const handout = {...normalized.handoutConfig}
	const section = handout.sections.find(item => item.id === sectionId)
	const list = kind === "rules" ? section?.rules : kind === "focusOptions" ? handout.focusOptions : handout.sections
	if (!list) return normalized
	const items = [...list]
	if (operation === "add") {
		const id = `${kind}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
		items.push(kind === "rules" ? {id, text: ""} : kind === "focusOptions" ? {id, title: "New focus", sectionId: handout.sections[0]?.id || ""} : {id, title: "New section", rules: []})
	} else if (operation === "remove") {
		if (index >= 0 && index < items.length) items.splice(index, 1)
	} else if ((operation === "up" && index > 0) || (operation === "down" && index < items.length - 1)) {
		const destination = operation === "up" ? index - 1 : index + 1
		items.splice(destination, 0, items.splice(index, 1)[0])
	}
	if (kind === "rules") handout.sections = handout.sections.map(item => item.id === sectionId ? {...item, rules: items} : item)
	else handout[kind] = items
	if (kind === "sections" && operation === "remove") handout.focusOptions = handout.focusOptions.map(option => items.some(item => item.id === option.sectionId) ? option : {...option, sectionId: items[0]?.id || ""})
	if (kind === "focusOptions" && !items.some(item => item.id === handout.defaultFocusId)) handout.defaultFocusId = items[0]?.id || ""
	return {...normalized, handoutConfig: handout}
}

export function getHandoutSummary(payload = {}, config = {}) {
	const snapshot = payload.form?.handoutConfig ? payload.form : normalizeHandoutConfig(config)
	const view = getHandoutView(snapshot, payload)
	return {headline: `${view.focus?.title || snapshot.title || "Handout"} · ${view.isReady ? view.handout.labels?.ready || "Ready" : view.handout.labels?.draft || "Draft"}`, form: JSON.parse(JSON.stringify(snapshot)), answers: {...payload.answers}, selectedFocusId: payload.selectedFocusId || view.focus?.id || "", acknowledged: view.acknowledged, acknowledgedAt: view.acknowledgedAt, readiness: view.isReady ? "ready" : "draft", missing: view.missing, scoreResult: null}
}

export function reconcileHandoutAcknowledgement(config = {}, payload = {}) {
	const current = getHandoutView(config, {...payload, context: config.handoutConfig?.context || "", selectedFocusId: config.handoutConfig?.defaultFocusId || ""})
	return {...payload, acknowledged: current.acknowledged, acknowledgedAt: current.acknowledgedAt, acknowledgedSignature: current.acknowledged ? current.confirmationSignature : ""}
}
